// Per-customer actuals. The ONLY place that talks to the `customer_actuals`
// table. A hub's actual total is the SUM of its customers' actuals — see
// hubTotalsFromCustomerActuals (used by the dashboard accuracy + actuals page).

import { supabase } from "../../lib/supabase";
import { fetchAllPaged } from "../../lib/dbPaginate";

export interface CustomerActual {
  customerId: string;
  hubId: string;
  date: string; // YYYY-MM-DD
  actualVolume: number; // total
  cartons?: number | null; // optional per-package-type breakdown
  polybags?: number | null;
  irregular?: number | null;
}

interface CustomerActualRow {
  id: string;
  account_id: string;
  customer_id: string;
  hub_id: string;
  date: string;
  actual_volume: number;
  cartons: number | null;
  polybags: number | null;
  irregular: number | null;
}

/** DB row -> app CustomerActual. */
export function rowToCustomerActual(row: CustomerActualRow): CustomerActual {
  return {
    customerId: row.customer_id,
    hubId: row.hub_id,
    date: row.date,
    actualVolume: Number(row.actual_volume),
    cartons: row.cartons == null ? null : Number(row.cartons),
    polybags: row.polybags == null ? null : Number(row.polybags),
    irregular: row.irregular == null ? null : Number(row.irregular),
  };
}

/** App CustomerActual -> DB row fields. */
export function customerActualInputToRow(accountId: string, a: CustomerActual) {
  return {
    account_id: accountId,
    customer_id: a.customerId,
    hub_id: a.hubId,
    date: a.date,
    actual_volume: a.actualVolume,
    cartons: a.cartons ?? null,
    polybags: a.polybags ?? null,
    irregular: a.irregular ?? null,
  };
}

/** Key for a hub-day total: `${hubId}|${date}`. */
export function hubDayKey(hubId: string, date: string): string {
  return `${hubId}|${date}`;
}

/** Sum customer actuals into hub-day totals (a hub actual = sum of its customers'). */
export function hubTotalsFromCustomerActuals(rows: CustomerActual[]): Map<string, number> {
  const m = new Map<string, number>();
  for (const r of rows) {
    const k = hubDayKey(r.hubId, r.date);
    m.set(k, (m.get(k) ?? 0) + r.actualVolume);
  }
  return m;
}

/** Load all customer actuals in the account within a date range (inclusive). */
export async function listAccountCustomerActuals(
  accountId: string,
  startDate: string,
  endDate: string
): Promise<CustomerActual[]> {
  // Paged: 50 customers × 6 months easily exceeds PostgREST's 1,000-row cap.
  const rows = await fetchAllPaged<CustomerActualRow>((from, to) =>
    supabase
      .from("customer_actuals")
      .select("*")
      .eq("account_id", accountId)
      .gte("date", startDate)
      .lte("date", endDate)
      .order("id")
      .range(from, to)
  );
  return rows.map(rowToCustomerActual);
}

/** Delete every customer actual for the account (irreversible). */
export async function deleteAllCustomerActuals(accountId: string): Promise<void> {
  const { error } = await supabase.from("customer_actuals").delete().eq("account_id", accountId);
  if (error) throw error;
}

/** Upsert customer actuals (one row per customer/hub/date). */
export async function saveCustomerActuals(
  accountId: string,
  entries: CustomerActual[]
): Promise<void> {
  if (entries.length === 0) return;
  const rows = entries.map((e) => customerActualInputToRow(accountId, e));
  const { error } = await supabase
    .from("customer_actuals")
    .upsert(rows, { onConflict: "customer_id,hub_id,date" });
  if (error) throw error;
}
